/**
 * Dashboard Utilities
 * Date/timezone handling and data processing utilities for the dashboard
 */

import { format, startOfDay, startOfMonth, startOfYear } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";

// ============================================================================
// TIMEZONE CONFIGURATION
// ============================================================================

/**
 * Default timezone fallback if company timezone is not provided
 */
const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Converts a GMT timestamp to a Date object in the reference timezone.
 * Uses company master timezone.
 *
 * @param {string} gmtTimestamp - ISO timestamp in GMT (e.g., "2026-01-02T20:14:55.984Z")
 * @param {string} timezone - IANA timezone string (e.g., "America/Toronto")
 * @returns {Date} - Date object adjusted to reference timezone
 */
export function toReferenceTimezone(gmtTimestamp, timezone = DEFAULT_TIMEZONE) {
    return utcToZonedTime(new Date(gmtTimestamp), timezone);
}

/**
 * Gets the current date/time in the specified timezone
 * @param {string} timezone - IANA timezone string
 * @returns {Date}
 */
export function getCurrentDateInTimezone(timezone = DEFAULT_TIMEZONE) {
    return utcToZonedTime(new Date(), timezone);
}

/**
 * Converts a local date in a timezone to UTC ISO string
 * @param {Date} localDate - Date in local timezone
 * @param {string} timezone - IANA timezone string
 * @returns {string} - ISO string in UTC (e.g., "2026-02-03T05:00:00.000Z")
 */
function toUTCISOString(localDate, timezone) {
    const utcDate = zonedTimeToUtc(localDate, timezone);
    return utcDate.toISOString();
}

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

/**
 * Gets date boundaries for a given period in the company's timezone.
 * Returns ISO strings in UTC for GraphQL 'between' queries.
 *
 * @param {'today' | 'currentMonth' | 'ytd' | 'lastYear'} period
 * @param {string} timezone - IANA timezone string (e.g., "America/Toronto")
 * @returns {{
 *   startDateISO: string,
 *   endDateISO: string,
 *   startDate: Date,
 *   endDate: Date,
 *   year: number
 * }}
 */
export function getDateRangeForPeriod(period, timezone = DEFAULT_TIMEZONE) {
    const now = new Date();
    const nowInTz = utcToZonedTime(now, timezone);
    const currentYear = nowInTz.getFullYear();
    const lastYear = currentYear - 1;

    switch (period) {
        case "today": {
            // Start of today in company timezone
            const todayStartLocal = startOfDay(nowInTz);
            const startDateISO = toUTCISOString(todayStartLocal, timezone);
            const endDateISO = now.toISOString(); // Current time in UTC

            return {
                startDateISO,
                endDateISO,
                startDate: todayStartLocal,
                endDate: nowInTz,
                year: currentYear
            };
        }
        case "currentMonth": {
            // Start of current month in company timezone
            const monthStartLocal = startOfMonth(nowInTz);
            const startDateISO = toUTCISOString(monthStartLocal, timezone);
            const endDateISO = now.toISOString();

            return {
                startDateISO,
                endDateISO,
                startDate: monthStartLocal,
                endDate: nowInTz,
                year: currentYear
            };
        }
        case "ytd": {
            // Start of current year in company timezone
            const yearStartLocal = startOfYear(nowInTz);
            const startDateISO = toUTCISOString(yearStartLocal, timezone);
            const endDateISO = now.toISOString();

            return {
                startDateISO,
                endDateISO,
                startDate: yearStartLocal,
                endDate: nowInTz,
                year: currentYear
            };
        }
        case "lastYear": {
            // Full previous calendar year in company timezone
            // Start: Jan 1 of last year at midnight in company timezone
            const lastYearStartLocal = new Date(lastYear, 0, 1, 0, 0, 0, 0);
            const startDateISO = toUTCISOString(lastYearStartLocal, timezone);

            // End: Jan 1 of current year at midnight in company timezone
            // This captures all of last year up to but not including current year
            const currentYearStartLocal = new Date(
                currentYear,
                0,
                1,
                0,
                0,
                0,
                0
            );
            const endDateISO = toUTCISOString(currentYearStartLocal, timezone);

            return {
                startDateISO,
                endDateISO,
                startDate: lastYearStartLocal,
                endDate: currentYearStartLocal,
                year: lastYear
            };
        }
        default:
            throw new Error(`Unknown period: ${period}`);
    }
}

// ============================================================================
// CHARGE TYPE CLASSIFICATION
// ============================================================================

export const CHARGE_TYPES = {
    TIP: "tip",
    ADDITIONAL: "additional",
    REGULAR: "regular"
};

/**
 * Classifies a ClientCharge based on its description field
 *
 * @param {Object} charge - ClientCharge record
 * @returns {string} - One of CHARGE_TYPES values
 */
export function classifyCharge(charge) {
    const description = charge.description || "";

    if (description.startsWith("Client generated tip amount")) {
        return CHARGE_TYPES.TIP;
    }

    if (description.startsWith("Additional Charge") ||
        description.startsWith("Misc Billing")) {
        return CHARGE_TYPES.ADDITIONAL;
    }

    return CHARGE_TYPES.REGULAR;
}

// ============================================================================
// DATA PROCESSING - CLIENT CHARGES
// ============================================================================

/**
 * Processes ClientCharge records and aggregates by currency.
 * Classifies charges into regular, tips, and additional charges.
 *
 * @param {Array} charges - Array of ClientCharge records
 * @returns {{
 *   byCurrency: { [currency: string]: { booked: number, refunds: number } },
 *   tipsByCurrency: { [currency: string]: number },
 *   additionalChargesByCurrency: { [currency: string]: number },
 *   tips: number,
 *   nonTipCharges: number,
 *   uniqueOrderIds: Set<string>,
 *   uniqueClientIds: Set<string>,
 *   totalOrders: number,
 *   totalClients: number
 * }}
 */
export function processClientCharges(charges) {
    const result = {
        byCurrency: {},
        tipsByCurrency: {},
        additionalChargesByCurrency: {},
        tips: 0,
        nonTipCharges: 0,
        uniqueOrderIds: new Set(),
        uniqueClientIds: new Set()
    };

    if (!charges || !Array.isArray(charges)) {
        return {
            ...result,
            totalOrders: 0,
            totalClients: 0
        };
    }

    charges.forEach((charge) => {
        const currency = charge.currency || "USD";
        const chargeType = classifyCharge(charge);
        const amount = charge.amount || 0;

        if (chargeType === CHARGE_TYPES.TIP) {
            // Track tips by currency
            result.tipsByCurrency[currency] =
                (result.tipsByCurrency[currency] || 0) + amount;
            result.tips += amount;
        } else if (chargeType === CHARGE_TYPES.ADDITIONAL) {
            // Track additional charges by currency
            result.additionalChargesByCurrency[currency] =
                (result.additionalChargesByCurrency[currency] || 0) + amount;
            result.nonTipCharges += amount;
        } else {
            // Regular charge - process as before
            const refundedAmount = charge.amount_refunded || 0;
            const hasRefund =
                Boolean(charge.stripe_refund_charge) || refundedAmount > 0;

            // Initialize currency bucket if not exists
            if (!result.byCurrency[currency]) {
                result.byCurrency[currency] = { booked: 0, refunds: 0 };
            }

            // Add to booked revenue (regular charges before refunds)
            result.byCurrency[currency].booked += amount;

            // Track refunds
            if (hasRefund) {
                result.byCurrency[currency].refunds += refundedAmount;
            }

            // Track unique orders and clients (exclude refunded charges)
            if (!hasRefund) {
                if (charge.orderId) {
                    result.uniqueOrderIds.add(charge.orderId);
                }
                if (charge.clientId) {
                    result.uniqueClientIds.add(charge.clientId);
                }
            }
        }
    });

    return {
        ...result,
        totalOrders: result.uniqueOrderIds.size,
        totalClients: result.uniqueClientIds.size
    };
}

// ============================================================================
// MONTHLY AGGREGATION FOR CHARTS
// ============================================================================

const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];

/**
 * Aggregates ClientCharge data by month for a given year.
 * Classifies charges into regular, tips, and additional charges.
 *
 * @param {Array} charges - Array of ClientCharge records
 * @param {number} year - Year to aggregate for
 * @param {string} timezone - IANA timezone string
 * @returns {Array<{ month: number, monthName: string, booked: number, refunds: number, tips: number, tipsByCurrency: Object, nonTipCharges: number, additionalChargesByCurrency: Object }>}
 */
export function aggregateClientChargesByMonth(
    charges,
    year,
    timezone = DEFAULT_TIMEZONE
) {
    // Initialize monthly data structure
    const monthlyData = MONTH_NAMES.map((name, index) => ({
        month: index,
        monthName: name,
        booked: 0,
        refunds: 0,
        tips: 0,
        tipsByCurrency: {},
        nonTipCharges: 0,
        additionalChargesByCurrency: {}
    }));

    if (!charges || !Array.isArray(charges)) {
        return monthlyData;
    }

    charges.forEach((charge) => {
        const date = toReferenceTimezone(charge.createdAt, timezone);
        if (date.getFullYear() === year) {
            const monthIndex = date.getMonth();
            const chargeType = classifyCharge(charge);
            const currency = charge.currency || "USD";
            const amount = charge.amount || 0;

            if (chargeType === CHARGE_TYPES.TIP) {
                monthlyData[monthIndex].tips += amount;
                monthlyData[monthIndex].tipsByCurrency[currency] =
                    (monthlyData[monthIndex].tipsByCurrency[currency] || 0) +
                    amount;
            } else if (chargeType === CHARGE_TYPES.ADDITIONAL) {
                monthlyData[monthIndex].nonTipCharges += amount;
                monthlyData[monthIndex].additionalChargesByCurrency[currency] =
                    (monthlyData[monthIndex].additionalChargesByCurrency[
                        currency
                    ] || 0) + amount;
            } else {
                // Regular charge - add to booked revenue
                monthlyData[monthIndex].booked += amount;
                monthlyData[monthIndex].refunds += charge.amount_refunded || 0;
            }
        }
    });

    return monthlyData;
}

/**
 * Combines ClientCharge monthly data for chart display.
 * Tips and additional charges are now included in clientChargeMonthly.
 *
 * @param {Array} clientChargeMonthly - Monthly aggregated ClientCharge data (includes tips and additional charges)
 * @returns {Array<{ month: number, monthName: string, booked: number, refunds: number, netBooked: number, additional: number, gross: number, net: number, tips: number }>}
 */
export function combineMonthlyData(clientChargeMonthly) {
    return MONTH_NAMES.map((name, index) => {
        const data = clientChargeMonthly[index] || {
            booked: 0,
            refunds: 0,
            tips: 0,
            nonTipCharges: 0
        };

        const netBooked = data.booked - data.refunds;
        const additional = data.nonTipCharges;
        const gross = data.booked + additional;
        const net = gross - data.refunds;

        return {
            month: index,
            monthName: name,
            booked: data.booked,
            refunds: data.refunds,
            netBooked: netBooked,        // For chart: Booked - Refunds
            additional: additional,       // For chart stacking
            gross: gross,                 // Booked + Additional
            net: net,                     // Gross - Refunds
            tips: data.tips
        };
    });
}

// ============================================================================
// GROWTH CALCULATIONS
// ============================================================================

/**
 * Calculates percentage growth between two values
 *
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value
 * @returns {{ percentage: number, isPositive: boolean, isZero: boolean }}
 */
export function calculateGrowth(current, previous) {
    if (previous === 0) {
        if (current === 0) {
            return { percentage: 0, isPositive: true, isZero: true };
        }
        // Infinite growth from 0 - show 100%
        return { percentage: 100, isPositive: true, isZero: false };
    }

    const percentage = ((current - previous) / previous) * 100;
    return {
        percentage: Math.abs(percentage),
        isPositive: percentage >= 0,
        isZero: percentage === 0
    };
}

// ============================================================================
// CURRENCY FORMATTING
// ============================================================================

/**
 * Gets currency symbol for a given currency code
 *
 * @param {string} currency - Currency code (USD, CAD, AUD, GBP, etc.)
 * @returns {string}
 */
export function getCurrencySymbolForDashboard(currency) {
    const symbols = {
        USD: "$",
        CAD: "CA$",
        AUD: "A$",
        GBP: "£",
        BBD: "$",
        PHP: "₱"
    };
    return symbols[currency] || "$";
}

/**
 * Formats a currency value with symbol and proper formatting
 *
 * @param {number} value - Amount to format
 * @param {string} currency - Currency code
 * @returns {string}
 */
export function formatCurrencyValue(value, currency = "USD") {
    const symbol = getCurrencySymbolForDashboard(currency);
    // Ensure value is a valid number, default to 0 if not
    const numericValue = typeof value === "number" && !isNaN(value) ? value : 0;
    const formatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue);
    return `${symbol}${formatted}`;
}

// ============================================================================
// DATA FILTERING BY DATE
// ============================================================================

/**
 * Filters records to only include those within the specified date range.
 *
 * NOTE: This filtering is redundant when using 'between' queries with precise
 * UTC boundaries. It is kept as a safety net for edge cases where the query
 * might return records slightly outside the expected range due to timezone
 * conversion nuances.
 *
 * @param {Array} records - Array of records with createdAt field
 * @param {Date} startDate - Start of date range (inclusive)
 * @param {Date} endDate - End of date range (inclusive)
 * @param {string} timezone - IANA timezone string
 * @returns {Array}
 */
export function filterRecordsByDateRange(
    records,
    startDate,
    endDate,
    timezone = DEFAULT_TIMEZONE
) {
    if (!records || !Array.isArray(records)) {
        return [];
    }

    return records.filter((record) => {
        const recordDate = toReferenceTimezone(record.createdAt, timezone);
        return recordDate >= startDate && recordDate <= endDate;
    });
}
